import React, { useEffect } from "react";
import { Box, Typography, Button, Card, CardContent } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ROLES } from "../../component/common/Routes/AppRoutes";
import {
  fetchRoleDetailsRequest,
  resetRoleState,
  updateRoleRequest
} from "../../redux/Roles/actions/RolesActions";
import TextInputField from "../../component/ui/FormFields/TextInputField";
import PermissionsTable from "../../component/ui/PermissionsTable";
import StatusRadioGroup from "../../component/ui/FormFields/StatusRadioGroup";
import Loading from "../../component/ui/Loading";
import CustomButton from "../../component/ui/FormFields/Button";

export default function EditRoleForm() {
  const location = useLocation();
  
       const page= location.state?.pageEdit?? 0;
  const dispatch = useDispatch();
  const searchQuery=location.state?.searchQuery??"";
  const roleData = location.state?.roleData || {};
  const roleDetail = useSelector((state) => state.roles || {});
  const viewLoading = roleDetail?.loading;
  const editLoading = roleDetail?.updateLoading;
  const id = roleData.id;
  const roleId = id;

  useEffect(() => {
    if (roleId) {
      dispatch(fetchRoleDetailsRequest(roleId));
    }
  }, [dispatch, roleId]);

  const status = roleData.status === 0 ? "Active" : "Inactive";
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors }
  } = useForm({
    defaultValues: {
      ...roleData,
      name: roleData.name,
      status: roleData.status === 0 ? "Active" : "Inactive",
      permissions: roleData.permissions || {}
    }
  });

  useEffect(() => {
    if (roleData) {
      setValue("roleName", roleData.name);
      setValue("status", roleData.status === 0 ? "Active" : "Inactive");
      setValue("permissions", roleData.permissions || {});
    }
  }, [roleData, setValue]);

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(resetRoleState());
  }, []);

  const handlePermissionChange = (category, perm, checked) => {
    // Special handling for invoices and reports
    if (category === "invoices" || category === "reports") {
      if (perm === "allaccess") {
        // Set allaccess and uncheck noaccess
        setValue(`permissions.${category}.allaccess`, checked);
        setValue(`permissions.${category}.noaccess`, false);
      } else if (perm === "noaccess") {
        // Set noaccess and uncheck allaccess
        setValue(`permissions.${category}.noaccess`, checked);
        setValue(`permissions.${category}.allaccess`, false);
      }
      return;
    }

    // Original logic for other categories
    const permissionsList = [
      "add",
      "view",
      "edit",
      "delete",
      "allaccess",
      "noaccess"
    ];

    if (perm === "allaccess") {
      // When allaccess is checked, enable all basic permissions
      permissionsList.forEach((key) => {
        if (key !== "allaccess" && key !== "noaccess") {
          setValue(`permissions.${category}.${key}`, checked);
        }
      });
      // Uncheck noaccess when allaccess is selected
      setValue(`permissions.${category}.noaccess`, false);
      setValue(`permissions.${category}.allaccess`, checked);
    } else if (perm === "noaccess") {
      // When noaccess is checked, disable all other permissions
      permissionsList.forEach((key) => {
        if (key !== "noaccess") {
          setValue(`permissions.${category}.${key}`, false);
        }
      });
      // Uncheck allaccess when noaccess is selected
      setValue(`permissions.${category}.allaccess`, false);
      setValue(`permissions.${category}.noaccess`, checked);
    } else {
      // For individual permissions
      setValue(`permissions.${category}.${perm}`, checked);

      // If any basic permission is checked, uncheck noaccess
      if (checked) {
        setValue(`permissions.${category}.noaccess`, false);
      }

      // Check if all basic permissions are checked
      const currentPermissions = getValues(`permissions.${category}`);
      const allChecked = ["add", "view", "edit", "delete"].every((key) => {
        return currentPermissions[key];
      });

      // Update allaccess based on basic permissions
      setValue(`permissions.${category}.allaccess`, allChecked);
    }
  };

  const onSubmit = async (data) => {
    const formattedData = [
      {
        name: data.roleName,
        status: data.status === "Active" ? "0" : "1",
        permissions: data.permissions
      }
    ];
    await dispatch(updateRoleRequest(formattedData, id));
  };

  useEffect(() => {
    if (roleDetail.success) {
      toast.success("Role Updated Successfully", {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          dispatch(resetRoleState());
          navigate(ROLES);
        }
      });
    }
  }, [roleDetail.success]);

  useEffect(() => {
    if (roleDetail.error) {
      toast.error(roleDetail.error, {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          dispatch(resetRoleState());
        }
      });
    }
  }, [roleDetail.error]);

  if (viewLoading) {
    return <Loading text="Loading Role Details..." />;
  }

  return (
    <>
      <ToastContainer />
      <Box className="role-permission-container">
        <Card>
          <CardContent className="card">
            <Typography
              variant="h5"
              gutterBottom
              className="form-heading"
              mb={3}
            >
              Edit Role
            </Typography>

            <Box className="role-permission-input-container">
              <Controller
                name="roleName"
                control={control}
                rules={{ required: "Role Name is required" }}
                render={({ field }) => (
                  <TextInputField
                    label="Role Name *"
                    fullWidth
                    placeholder="Enter Role Name"
                    error={!!errors.roleName}
                    helperText={errors.roleName?.message}
                    {...field}
                  />
                )}
              />
            </Box>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Box mb={3}>
                <PermissionsTable
                  control={control}
                  permissionsData={roleData.permissions || {}}
                  handlePermissionChange={handlePermissionChange}
                  disabledCategories={["invoices", "reports"]}
                />
              </Box>
              <Box className="role-status">
                <StatusRadioGroup
                  name="status"
                  control={control}
                  defaultValue={status}
                  rules={{ required: "Status is required" }}
                />
              </Box>

              <Box mt={3} className="role-permission-button-container">
                <Button
                  variant="contained"
                  color="error"
                        onClick={() => page!==0?navigate(`/roles`,{state:{page:page,searchQuery:searchQuery}}):navigate(-1)}
                  
                  className="cancel-button"
                >
                  Back
                </Button>
                <CustomButton
                  variant="contained"
                  className="success-button"
                  type="submit"
                  loading={editLoading}
                  text={"submit"}
                />
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
